// 9fbef606107a605d69c0edbcd8029e5d
import logger from '@tcp/core/src/utils/loggerInstance';
import { getPlaceSiteId, isSFCC } from '@tcp/core/src/utils';
import getRegisteredUser from '@tcp/core/src/queries/getRegisteredCustomer.query.graphql';
import { encode } from '@tcp/core/src/utils/base64.utils';
import { executeStatefulAPICall, executeStatefulGraphQLQuery } from '../../handler';
import { getAPIConfig } from '../../../utils';
import endpoints from '../../endpoints';

export const responseContainsErrors = (response) => {
  if (!response || !response.body) {
    return false;
  }
  const responseBody = response.body;
  return !!(
    responseBody.errorCode ||
    responseBody.errorMessageKey ||
    responseBody.errorKey ||
    (responseBody.errors && responseBody.errors.length > 0) ||
    (response.body.error && response.body.error.errorCode)
  );
};

export const ADDREESS_TYPE = {
  SHIPPING: 'Shipping',
  BILLING: 'Billing',
  SHIPPINGANDBILLING: 'ShippingAndBilling',
  MAILING: 'Mailing',
};

export const parseBoolean = (bool) => {
  return bool === true || bool === '1' || (bool || '').toUpperCase() === 'TRUE';
};

/**
 * This function converts string to an array.
 * @param {string} string This param receives parse string
 * @param {string} delimeter This param splits the string using delimeter
 */
export const stringToArray = (string, delimeter) => {
  try {
    const obj = { ...string };
    return Object.keys(obj).map((key) => obj[key].split(delimeter));
  } catch (e) {
    return [];
  }
};

export const getDefaultPlccAddress = (addresses, defaultAddressId) => {
  const plccAddress =
    (addresses.length > 0 && addresses.find((address) => address.addressId === defaultAddressId)) ||
    null;

  if (plccAddress) {
    const {
      addressId = '',
      firstName = '',
      lastName = '',
      address1 = '',
      address2 = '',
      city = '',
      stateCode = '',
      countryCode = '',
      postalCode = '',
      phone = '',
    } = plccAddress;
    return {
      addressId,
      address: {
        firstName,
        lastName,
        addressLine1: address1,
        addressLine2: address2,
        city,
        state: stateCode,
        country: countryCode || 'US',
        zipCode: postalCode,
      },
      phoneNumber: phone,
    };
  }
  return null;
};

export const getProfileAddress = (body) => {
  const {
    addressId = '',
    city = '',
    state = '',
    phoneMobile = '',
    addressLine = [],
    country = '',
    zipCode = '',
    mailingAddressId = '',
  } = body;
  return {
    addressId: mailingAddressId || addressId || '',
    type: ADDREESS_TYPE.MAILING,
    isComplete: !!(city && state),
    phoneNumber: phoneMobile,
    address: {
      addressId: mailingAddressId || addressId || '',
      address1: (addressLine && addressLine[0]) || '',
      address2: (addressLine && addressLine[1]) || '',
      city,
      state,
      country,
      zipCode,
    },
  };
};

// functionality pending.
export const getContextAttributes = (getRegisteredCustomer) => {
  return (
    getRegisteredCustomer?.contextAttribute &&
    getRegisteredCustomer?.contextAttribute.reduce((obj, item) => {
      const {
        attributeName,
        attributeValue: [
          {
            value: [attrValue],
          },
        ],
      } = item;

      // eslint-disable-next-line no-param-reassign
      obj[attributeName] = attrValue; // Single value since it is an attribute
      return obj;
    }, {})
  );
};

export const getIfUserLoggedIn = (isRegistered, isRememberedUser) => {
  return parseBoolean(isRegistered) && !parseBoolean(isRememberedUser);
};

export const getIfUserRemembered = (isRegistered, isRememberedUser) => {
  return parseBoolean(isRegistered) && parseBoolean(isRememberedUser);
};

export const getSurveyAnswers = (userSurvey) => {
  return userSurvey ? stringToArray(JSON.parse(userSurvey), '|') : [];
};

export const formatAddressBookResponse = (arr) => {
  let containsDefault = false;

  const addresses = (arr || []).map((address) => {
    containsDefault = containsDefault || parseBoolean(address.preferred);
    return {
      addressId: address.addressId,
      addressKey: address.nickName,
      address: {
        firstName: address.firstName,
        lastName: address.lastName,
        addressLine1: address.addressLine ? address.addressLine[0] : null,
        addressLine2: address.addressLine ? address.addressLine[1] : null,
        city: address.city,
        state: address.state,
        country: address.country,
        zipCode: address.zipCode,
      },
      emailAddress: address.email1,
      phoneNumber: address.phone1,
      type: address.addressType,
      isDefault: parseBoolean(address.preferred),
    };
  });

  if (!containsDefault && addresses.length) {
    addresses[0].isDefault = true;
  }
  return addresses;
};

const logonErrorHandling = (res) => {
  if (responseContainsErrors(res)) {
    return {
      success: false,
      ...res.body,
    };
  }
  return {
    success: true,
  };
};

const formatResponse = (body, emailAddress) => {
  const { status, data } = body || {};
  const {
    success: isEmailExist,
    count,
    phoneNumber,
    token,
    vstatus,
    locked,
    otpLimit,
    lockTime,
    phoneCountryCode,
  } = data || {};
  // if email is present
  if (status?.toLowerCase() === 'success' && isEmailExist === 'true') {
    return {
      status: body?.status?.toLowerCase(),
      emailAddress,
      phoneNumber,
      token,
      isExistingUser: isEmailExist === 'true',
      vstatus,
      locked,
      otpLimit,
      lockTime,
      phoneCountryCode,
    };
  }
  // if email does not exist
  if (status?.toLowerCase() === 'success' && isEmailExist === 'false') {
    return {
      status: 'fail',
      emailAddress,
      error: {
        errorCode: 'EMAIL_DOES_NOT_EXIST',
        emailRetry: count,
      },
    };
  }
  // Other API error
  if (body && body.errors && body.errors.length > 0) {
    const { errorCode, errorParameters } = body.errors[0] || {};
    const { emailRetry } = errorParameters || {};

    return {
      status: 'fail',
      emailAddress: errorCode === '_ERR_INVALID_EMAIL' ? '' : emailAddress,
      error: {
        errorCode,
        emailRetry,
      },
    };
  }

  return {
    status: 'fail',
    emailAddress,
    error: {
      errorCode: 'UNKNOWN_ERROR',
    },
  };
};

export const login = ({
  emailAddress,
  password,
  rememberMe,
  recaptchaToken,
  userId,
  mergeCart,
  useSFCCAPI = false,
  isApplyPlccCardFlow = false,
  changePasswordRequest,
  isSfccPlatform = true,
  otp,
  isOTpScreenEnabled,
  isFromCreateAccountFlow,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const apiConfig = getAPIConfig();
  const { sfccLogon, logon, plccLogin, validateLoginOTP, validateCreateAccountLoginOTP } =
    endpoints;

  const getSFCCHeader = () => {
    return useSFCCAPI && !isSFCC() ? { cp: true } : {};
  };

  const payload = {
    body: {
      storeId: apiConfig.storeId,
      logonId1: emailAddress.trim(),
      logonPassword1: password,
      rememberCheck: rememberMe || false,
      rememberMe: rememberMe || false,
      requesttype: 'ajax',
      reLogonURL: 'TCPAjaxLogonErrorView',
      URL: 'TCPAjaxLogonSuccessView',
      registryAccessPreference: 'Public',
      calculationUsageId: -1,
      createIfEmpty: 1,
      deleteIfEmpty: '*',
      fromOrderId: '*',
      toOrderId: '.',
      updatePrices: 0,
      reCaptcha: recaptchaToken,
      mergeCart,
    },
    header: {
      siteId: getPlaceSiteId(),
      isSfccPlatform,
      ...getSFCCHeader(),
    },
    webService: useSFCCAPI ? sfccLogon : logon,
  };

  if (userId) {
    payload.body.userId = userId;
  } else {
    const GUEST_USER_ID = '-1002';
    payload.body.userId = GUEST_USER_ID;
  }
  const plccPayload = {};
  if (changePasswordRequest && isApplyPlccCardFlow) {
    plccPayload.body = {
      changePasswordRequest,
      loginRequest: payload.body,
    };
    plccPayload.header = payload.header;
    plccPayload.webService = plccLogin;
    plccPayload.body.loginRequest.logonPassword1 = changePasswordRequest.currentPassword;
  }
  if (typeof otp !== 'undefined' && otp) {
    payload.body.logonPassword1 = otp;
    payload.webService =
      isOTpScreenEnabled && isFromCreateAccountFlow
        ? validateCreateAccountLoginOTP
        : validateLoginOTP;
  }
  const payloadType = Object.keys(plccPayload).length > 0 ? plccPayload : payload;
  return executeStatefulAPICall(payloadType)
    .then((res) => logonErrorHandling(res))
    .catch((err) => {
      throw err;
    });
};

export const emailVerify = ({ emailAddress, recaptchaToken }) => {
  const { URI, method } = endpoints.emailVerify;
  const encodedEmail = emailAddress && encode(emailAddress.toUpperCase());
  const webService = {
    URI: `${URI}${encodedEmail}`,
    method,
  };

  const payload = {
    webService,
  };
  if (recaptchaToken) {
    payload.header = {
      'recaptcha-token': recaptchaToken,
    };
  }
  return executeStatefulAPICall(payload)
    .then((res) => {
      const { body } = res || {};
      return formatResponse(body, emailAddress);
    })
    .catch((err) => {
      const { errorResponse } = err || {};
      return formatResponse(errorResponse, emailAddress);
    });
};

export const generateLoginOTP = (
  { otpToken, recaptchaToken, isResend = false, otpChannel },
  isOTpScreenEnabled,
  isFromCreateAccountFlow
) => {
  const payload = {
    header: {
      siteId: getPlaceSiteId(),
    },
    webService:
      isOTpScreenEnabled && isFromCreateAccountFlow
        ? endpoints.generateCreateAccountOTP
        : endpoints.generateLoginOTP,
    body: {
      token: otpToken,
      isResend,
      otpChannel,
    },
  };

  if (recaptchaToken) {
    payload.header = {
      ...payload.header,
      'recaptcha-token': recaptchaToken,
    };
  }
  return executeStatefulAPICall(payload)
    .then((res) => logonErrorHandling(res))
    .catch((err) => {
      throw err;
    });
};

export const getProfile = ({ refresh = false }) => {
  const payload = {
    query: getRegisteredUser,
    fetchPolicy: 'no-cache',
  };
  if (refresh) {
    payload.header = {
      refresh,
    };
  }
  // context attributes pending.
  return executeStatefulGraphQLQuery(payload)
    .then((res) => {
      const { getRegisteredCustomer } = res?.data;
      const {
        firstName = '',
        lastName = '',
        phoneHome = '',
        phoneMobile = '',
        email = '',
        favoriteStore = '',
        isExpress = '',
        currency,
        hasPLcc = false,
        isPlccIC = false,
        currentTier = '',
        currentTierEffectiveDate = '',
        currentTierExpirationDate = '',
        targetSpendForNextTier = '',
        redeemablePoints = 0,
        airMilesAccount = '',
        associateId = '',
        xlanguage = '',
        userSurvey = '',
        addresses = [],
        billingAddressId = '',
        currencyToNextReward = 100,
        currencyBalance = 0,
        totalReward = 0,
        xcountry = '',
        profilePercentageComplete = '',
        isRegistered = '',
        isRememberedUser = '',
        wishListId = '',
        UserId,
        cMPRID = '',
        wicPlccId = '',
        cardLast4 = '',
        userBirthday = '',
        sflListId = '',
        shippingAddressId = '',
        totalSpend = 0,
        currentPerkLevel = '',
        nextPerkLevel = '',
        spendLeftForNextPerk = 0,
        plccPromoEligible = false,
        phoneCountryCode = '',
      } = getRegisteredCustomer;
      const userLoggedIn = getIfUserLoggedIn(isRegistered, isRememberedUser);
      const userRemembered = getIfUserRemembered(isRegistered, isRememberedUser);
      const surveyAnswers = getSurveyAnswers(userSurvey);
      const contextAttributes = getContextAttributes(getRegisteredCustomer);

      return {
        firstName,
        lastName,
        userId: UserId,
        phone: phoneMobile || phoneHome,
        email,
        isLoggedin: userLoggedIn,
        isRemembered: userRemembered,
        isPlcc: parseBoolean(hasPLcc),
        isPlccIC: parseBoolean(isPlccIC),
        currentTier,
        currentTierEffectiveDate,
        currentTierExpirationDate,
        targetSpendForNextTier,
        redeemablePoints,
        favStoreId: favoriteStore,
        isExpressEligible: parseBoolean(isExpress),
        country: xcountry || 'US', // Response is received on the basis of iShippingCookie, for app it would be US only.
        currency: currency || 'USD', // Response is received on the basis of iShippingCookie, for app it would be US only.
        airmilesAccountNumber: airMilesAccount, // Coming "", pending on BE
        myPlaceNumber: cMPRID,
        plccCardId: wicPlccId,
        plccCardNumber: cardLast4,
        associateId,
        language: (xlanguage || 'en_US').substr(0, 2), // Response is received on the basis of iShippingCookie, for app it would be US only.
        surveyAnswers,
        userBirthday,
        defaultPlccAddress: getDefaultPlccAddress(addresses, billingAddressId),
        defaultAddress: getDefaultPlccAddress(addresses, shippingAddressId),
        profileAddress: getProfileAddress(getRegisteredCustomer),
        pointsToNextReward: currencyToNextReward || 100,
        currentPoints: currencyBalance || 0,
        currentMonthsRewards: totalReward,
        contextAttributes, // Field pending on BE
        userProfileState: {
          profileCompletion: profilePercentageComplete,
        },
        defaultWishListId: wishListId,
        sflListId,
        addresses,
        shippingAddressId,
        totalSpend,
        currentPerkLevel,
        nextPerkLevel,
        spendLeftForNextPerk,
        plccPromoEligible,
        phoneCountryCode,
      };
    })
    .catch((e) => {
      throw e;
    });
};

export const cartSync = () => {
  const apiConfig = getAPIConfig();
  const { storeId } = apiConfig;
  const payload = {
    header: {
      siteId: getPlaceSiteId(),
      storeId,
    },
    webService: endpoints.cartSync,
  };

  return executeStatefulAPICall(payload)
    .then((res) => {
      if (responseContainsErrors(res)) {
        throw new Error(res);
      } else {
        return res;
      }
    })
    .catch((e) => {
      logger.error({
        error: `Exception in cart Sync API Call: ${JSON.stringify(e)}`,
      });
    });
};
